(() => {
  'use strict';

  const roots = document.querySelectorAll(
    '[data-f2w-venues]'
  );

  if (!roots.length) {
    return;
  }

  const normalize = (value) => {
    return String(value || '')
      .toLocaleLowerCase('nl-NL')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  };

  const uniqueSorted = (values) => {
    const result = new Map();

    values
      .flatMap((value) => {
        return Array.isArray(value)
          ? value
          : [value];
      })
      .map((value) => {
        return String(value || '').trim();
      })
      .filter(Boolean)
      .forEach((value) => {
        const key = normalize(value);

        if (!result.has(key)) {
          result.set(key, value);
        }
      });

    return [...result.values()].sort((a, b) => {
      return a.localeCompare(b, 'nl-NL');
    });
  };

  const referenceLabel = (reference) => {
    return String(
      reference?.name?.value ||
      reference?.handle ||
      ''
    ).trim();
  };

  const nativeUrl = (value) => {
    if (!value) {
      return '';
    }

    try {
      const url = new URL(
        String(value),
        window.location.origin
      );

      if (
        url.protocol !== 'http:' &&
        url.protocol !== 'https:'
      ) {
        return '';
      }

      const root =
        window.Shopify?.routes?.root || '/';

      const prefix =
        root === '/'
          ? ''
          : root.replace(/\/$/, '');

      let path = url.pathname;

      if (/^\/[a-z]{2}(\/|$)/i.test(path)) {
        path = path.replace(/^\/[a-z]{2}/i, '');
      }

      if (
        prefix &&
        path.startsWith(`${prefix}/`)
      ) {
        path = path.slice(prefix.length);
      }

      return `${prefix}${path}${url.search}${url.hash}`;
    } catch (error) {
      return '';
    }
  };

  const countryFlagElement = (
    code,
    label
  ) => {
    const flagCode = String(code || '')
      .trim()
      .toLowerCase();

    const isNation =
      flagCode === 'gb-sct' ||
      flagCode === 'gb-eng' ||
      flagCode === 'gb-wls';

    if (
      !flagCode ||
      (flagCode.length !== 2 && !isNation)
    ) {
      return null;
    }

    const wrapper =
      document.createElement('span');
    wrapper.className = 'f2w-country-flag';

    const image =
      document.createElement('img');
    image.src =
      `https://flagcdn.com/w40/${flagCode}.png`;
    image.srcset =
      `https://flagcdn.com/w80/${flagCode}.png 2x`;
    image.alt = String(label || '');
    image.width = 20;
    image.height = 15;
    image.loading = 'lazy';
    image.decoding = 'async';

    wrapper.appendChild(image);
    wrapper.appendChild(
      document.createTextNode('\u00a0')
    );

    return wrapper;
  };

  const element = (tag, className, text) => {
    const node = document.createElement(tag);

    if (className) {
      node.className = className;
    }

    if (
      text !== undefined &&
      text !== null
    ) {
      node.textContent = text;
    }

    return node;
  };

  const setElementVisibility = (node, visible) => {
    if (!node) {
      return;
    }

    node.hidden = !visible;
    node.style.setProperty(
      'display',
      visible ? '' : 'none',
      'important'
    );
    node.setAttribute(
      'aria-hidden',
      visible ? 'false' : 'true'
    );
  };

  // Content query for one chunk of venues. Images come from
  // `resolved_image_url`, a plain url-type field (not a file reference),
  // so the Storefront API returns them directly in this same query. The
  // Storefront API cannot resolve file/image references on a metaobject
  // field, which is why no image field of that kind is requested here and
  // why there is no Liquid image-map pass anywhere in this section.
  const CHUNK_QUERY = `
    query F2WVenues(
      $cursor: String
      $language: LanguageCode!
    ) @inContext(language: $language) {
      metaobjects(
        type: "venue"
        first: 250
        after: $cursor
      ) {
        pageInfo {
          hasNextPage
          endCursor
        }

        nodes {
          id
          handle
          onlineStoreUrl

          name: field(key: "name") {
            value
          }

          venue_type: field(key: "venue_type") {
            value
          }

          city: field(key: "city") {
            value
          }

          resolved_image_url: field(key: "resolved_image_url") {
            value
          }

          country: field(key: "country") {
            reference {
              ... on Metaobject {
                handle

                name: field(key: "name") {
                  value
                }

                code: field(key: "code") {
                  value
                }
              }
            }
          }

        }
      }
    }
  `;

  // The Index app uses the same compact geographic row contract as Gyms.
  // The index supplies IDs and filter metadata; this targeted query supplies
  // the translated name, native URL and direct image URL for each card.
  const NODES_QUERY = `
    query F2WVenueNodes(
      $ids: [ID!]!
      $language: LanguageCode!
    ) @inContext(language: $language) {
      nodes(ids: $ids) {
        ... on Metaobject {
          id
          handle
          onlineStoreUrl

          name: field(key: "name") {
            value
          }

          venue_type: field(key: "venue_type") {
            value
          }

          city: field(key: "city") {
            value
          }

          resolved_image_url: field(key: "resolved_image_url") {
            value
          }

          country: field(key: "country") {
            reference {
              ... on Metaobject {
                handle

                name: field(key: "name") {
                  value
                }

                code: field(key: "code") {
                  value
                }
              }
            }
          }
        }
      }
    }
  `;

  roots.forEach((root) => {
    if (
      root.dataset.f2wInitialized === 'true'
    ) {
      return;
    }

    root.dataset.f2wInitialized = 'true';
    root.hidden = false;

    const token =
      root.dataset.storefrontToken || '';

    const shop =
      root.dataset.shopDomain ||
      window.Shopify?.shop ||
      window.location.hostname;

    const apiVersion =
      root.dataset.apiVersion ||
      '2024-10';

    const endpoint =
      root.dataset.storefrontEndpoint ||
      `https://${shop}/api/${apiVersion}/graphql.json`;

    const language = String(
      root.dataset.language ||
      document.documentElement?.lang ||
      'nl'
    )
      .split('-')[0]
      .trim()
      .toUpperCase() || 'NL';

    const perPage = Math.max(
      1,
      parseInt(root.dataset.perPage, 10) || 48
    );

    const countFormatter = new Intl.NumberFormat(root.dataset.language || document.documentElement?.lang || 'nl');
    root.querySelectorAll('[data-f2w-venue-snapshot-value]').forEach((value) => {
      const number = Number(value.textContent.trim());
      if (Number.isFinite(number)) value.textContent = countFormatter.format(number);
    });

    const searchInput = root.querySelector(
      '[data-f2w-venues-filter-search]'
    );

    const countrySelect = root.querySelector(
      '[data-f2w-venues-filter-country]'
    );

    const typeSelect = root.querySelector(
      '[data-f2w-venues-filter-type]'
    );
    const citySelect = root.querySelector('[data-f2w-venues-filter-city]');
    const countryPicker = root.querySelector('[data-f2w-venues-country-picker]');
    const countryQuick = root.querySelector('[data-f2w-venues-country-quick]');
    const countryMore = root.querySelector('[data-f2w-venues-country-more]');
    const countryPanel = root.querySelector('[data-f2w-venues-country-panel]');
    const countrySearch = root.querySelector('[data-f2w-venues-country-search]');
    const countryList = root.querySelector('[data-f2w-venues-country-list]');
    const cityPicker = root.querySelector('[data-f2w-venues-city-picker]');
    const cityQuick = root.querySelector('[data-f2w-venues-city-quick]');
    const cityMore = root.querySelector('[data-f2w-venues-city-more]');
    const cityPanel = root.querySelector('[data-f2w-venues-city-panel]');
    const citySearch = root.querySelector('[data-f2w-venues-city-search]');
    const cityList = root.querySelector('[data-f2w-venues-city-list]');
    const pinnedIds = ['gid://shopify/Metaobject/455120027914', 'gid://shopify/Metaobject/455120126218'];
    const pinnedCodes = ['NL', 'BE'];
    const pinnedNames = new Map();
    const countryCodes = new Map();
    const countryMoreLabel = countryMore.textContent.trim();
    countrySearch?.closest('.f2w-event-directory__filter')?.style.setProperty('display', 'none', 'important');
    citySearch?.closest('.f2w-event-directory__filter')?.style.setProperty('display', 'none', 'important');

    const resetButton = root.querySelector(
      '[data-f2w-venues-filter-reset]'
    );

    const resultCount = root.querySelector(
      '[data-f2w-venues-results-count]'
    );

    const loadingElement = root.querySelector(
      '[data-f2w-venues-loading]'
    );

    const errorElement = root.querySelector(
      '[data-f2w-venues-error]'
    );

    const emptyElement = root.querySelector(
      '[data-f2w-venues-empty]'
    );

    const resultsGrid = root.querySelector(
      '[data-f2w-venues-results]'
    );

    const loadMoreButton = root.querySelector(
      '[data-f2w-venues-load-more]'
    );

    const indexElement = root.querySelector(
      '[data-f2w-venue-index]'
    );

    const indexUrl = root.dataset.indexUrl || '';

    const labels = {
      results:
        root.dataset.labelResults || '',

      loading:
        root.dataset.labelLoading || '',

      error:
        root.dataset.labelError || '',

      empty:
        root.dataset.labelEmpty || '',
      countryEmpty: root.dataset.labelCountryEmpty || '',
      cityEmpty: root.dataset.labelCityEmpty || '',

      loadMore:
        root.dataset.labelLoadMore || '',

      view:
        root.dataset.labelView || '',

      types: {
        arena:
          root.dataset.labelTypeArena || '',

        stadium:
          root.dataset.labelTypeStadium || '',

        event_hall:
          root.dataset.labelTypeEventHall || '',

        gym_venue:
          root.dataset.labelTypeGymVenue || '',

        historic_venue:
          root.dataset.labelTypeHistoricVenue || ''
      }
    };

    // Incremental loading state. Since `venue` is now its own metaobject
    // type (no more filtering a shared 4,700-record sports_location
    // table down to the 12 that are venues), every fetched record is
    // already a venue; there is no canonical-type resolution step here.
    let venues = [];
    let visibleCount = perPage;
    let cursor = null;
    let exhausted = false;
    let loadError = false;

    const requestQuery = async (
      query,
      variables
    ) => {
      if (!token) {
        throw new Error(
          'Missing Storefront token'
        );
      }

      const response = await fetch(
        endpoint,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token':
              token
          },
          body: JSON.stringify({
            query,
            variables
          })
        }
      );

      if (!response.ok) {
        throw new Error(
          'Storefront request failed'
        );
      }

      const json = await response.json();

      if (
        Array.isArray(json.errors) &&
        json.errors.length
      ) {
        throw new Error(
          'Storefront query failed'
        );
      }

      return json.data || {};
    };

    const chunk = (items, size) => {
      const groups = [];

      for (let index = 0; index < items.length; index += size) {
        groups.push(items.slice(index, index + size));
      }

      return groups;
    };

    const parsePublishedIndex = (payload) => {
      if (
        payload?.sharded === true &&
        Array.isArray(payload.parts)
      ) {
        const countries = {};

        payload.parts
          .slice()
          .sort((a, b) => {
            return (Number(a?.part) || 0) -
              (Number(b?.part) || 0);
          })
          .forEach((part) => {
            if (
              part?.v !== 1 ||
              typeof part.countries !== 'object' ||
              part.countries === null
            ) {
              throw new Error(
                'Venue index part is invalid'
              );
            }

            Object.entries(part.countries).forEach(
              ([countryId, bucket]) => {
                if (!countries[countryId]) {
                  countries[countryId] = {
                    c: String(bucket?.c || ''),
                    r: []
                  };
                }

                if (Array.isArray(bucket?.r)) {
                  countries[countryId].r.push(
                    ...bucket.r
                  );
                }
              }
            );
          });

        return parsePublishedIndex({
          v: 1,
          count: payload.count,
          countries
        });
      }

      if (
        !payload ||
        payload.v !== 1 ||
        typeof payload.countries !== 'object' ||
        payload.countries === null
      ) {
        return null;
      }

      const records = [];

      Object.entries(payload.countries).forEach(
        ([countryId, bucket]) => {
          const rows = Array.isArray(bucket?.r)
            ? bucket.r
            : null;

          if (!rows) {
            throw new Error(
              'Venue index bucket is invalid'
            );
          }

          rows.forEach((row) => {
            if (
              !Array.isArray(row) ||
              row.length !== 5 ||
              typeof row[0] !== 'string' ||
              typeof row[1] !== 'string' ||
              typeof row[2] !== 'string' ||
              typeof row[3] !== 'string' ||
              !Array.isArray(row[4]) ||
              !row[4].every((handle) => typeof handle === 'string')
            ) {
              throw new Error(
                'Venue index row is invalid'
              );
            }

            records.push({
              id: row[0],
              countryId,
              countryCode: String(
                bucket?.c || ''
              ).trim().toUpperCase(),
              venueType: normalize(row[1]),
              cityId: row[2],
              city: row[3],
              sportHandles: row[4].slice()
            });
          });
        }
      );

      if (
        typeof payload.count !== 'number' ||
        payload.count !== records.length
      ) {
        throw new Error(
          'Venue index count mismatch'
        );
      }

      return records;
    };

    const readIndexPayload = async () => {
      const inlineValue = indexElement?.textContent?.trim();

      if (inlineValue) {
        try {
          return JSON.parse(inlineValue);
        } catch (error) {
          throw new Error(
            'Published Venue index is invalid'
          );
        }
      }

      if (!indexUrl) {
        return null;
      }

      const response = await fetch(indexUrl);

      if (!response.ok) {
        return null;
      }

      return response.json();
    };

    const typeLabel = (type) => {
      return labels.types[type] || type;
    };

    const mapVenue = (node, indexRecord = {}) => {
      const image = String(
        node.resolved_image_url?.value || ''
      ).trim();
      const countryReference =
        node.country?.reference;

      return {
        id: node.id || indexRecord.id || '',
        handle: node.handle || '',
        url: nativeUrl(node.onlineStoreUrl),
        name: String(
          node.name?.value || ''
        ).trim(),
        venueType: normalize(
          node.venue_type?.value ||
          indexRecord.venueType || ''
        ),
        city: String(
          node.city?.value ||
          indexRecord.city || ''
        ).trim(),
        country: countryReference
          ? referenceLabel(countryReference)
          : '',
        countryCode: String(
          countryReference?.code?.value ||
          indexRecord.countryCode || ''
        ).trim(),
        image
      };
    };

    // Fetches ONE page of venue records and appends matches to `venues`.
    const fetchNextChunk = async () => {
      if (exhausted) {
        return 0;
      }

      const data = await requestQuery(
        CHUNK_QUERY,
        {
          cursor: cursor || null,
          language
        }
      );

      const page = data.metaobjects;

      if (!page) {
        throw new Error(
          'No venue data returned'
        );
      }

      const nodes = page.nodes || [];

      const seenIds = new Set(
        venues.map((venue) => venue.id)
      );

      let added = 0;

      nodes.forEach((node) => {
        const venue = mapVenue(node);

        if (
          venue &&
          venue.name &&
          !seenIds.has(venue.id)
        ) {
          venues.push(venue);
          seenIds.add(venue.id);
          added += 1;
        }
      });

      if (page.pageInfo?.hasNextPage) {
        cursor = page.pageInfo.endCursor;

        if (!cursor) {
          throw new Error(
            'Missing next cursor'
          );
        }
      } else {
        exhausted = true;
      }

      venues.sort((a, b) => {
        return a.name.localeCompare(
          b.name,
          'nl-NL'
        );
      });

      return added;
    };

    const loadPublishedIndex = async () => {
      const payload = await readIndexPayload();

      if (!payload) {
        return false;
      }

      const indexRecords = parsePublishedIndex(
        payload
      );

      if (!indexRecords) {
        return false;
      }

      const recordById = new Map(
        indexRecords.map((record) => [
          record.id,
          record
        ])
      );
      const ids = indexRecords.map(
        (record) => record.id
      );
      const pages = [];

      for (let index = 0; index < ids.length; index += 750) {
        const group = chunk(
          ids.slice(index, index + 750),
          250
        );
        const results = await Promise.all(
          group.map((batch) => {
            return requestQuery(
              NODES_QUERY,
              {
                ids: batch,
                language
              }
            );
          })
        );

        pages.push(...results);
      }

      const mapped = pages
        .flatMap((page) => page.nodes || [])
        .map((node) => {
          return mapVenue(
            node,
            recordById.get(node?.id) || {}
          );
        })
        .filter((venue) => venue?.id && venue.name);

      const received = new Set(
        mapped.map((venue) => venue.id)
      );

      if (received.size !== ids.length) {
        throw new Error(
          'Published Venue index hydration was incomplete'
        );
      }

      venues = mapped.sort((a, b) => {
        return a.name.localeCompare(
          b.name,
          'nl-NL'
        );
      });
      cursor = null;
      exhausted = true;
      return true;
    };

    // Fallback for the period before the Index app has published its first
    // Venue payload, or when a published payload fails validation.
    const loadAllMatches = async () => {
      while (!exhausted) {
        await fetchNextChunk();
      }
    };

    const createCard = (venue) => {
      const article = element(
        'article',
        'f2w-event-card'
      );

      article.dataset.f2wVenueCard = '';

      const card = venue.url
        ? element(
            'a',
            'f2w-event-card__link'
          )
        : element(
            'div',
            'f2w-event-card__link'
          );

      if (venue.url) {
        card.href = venue.url;
      }

      const imageWrapper = element(
        'div',
        'f2w-event-card__image'
      );

      if (venue.image) {
        const image = document.createElement(
          'img'
        );

        image.src = venue.image;
        image.alt = venue.name;
        image.width = 1100;
        image.height = 825;
        image.loading = 'lazy';

        imageWrapper.appendChild(image);
      } else {
        const placeholder = element(
          'div',
          'f2w-event-card__image--placeholder'
        );

        placeholder.appendChild(
          element(
            'span',
            '',
            venue.name.slice(0, 1)
          )
        );

        imageWrapper.appendChild(
          placeholder
        );
      }

      const body = element(
        'div',
        'f2w-event-card__body'
      );

      const content = element(
        'div',
        'f2w-event-card__content'
      );

      if (venue.venueType) {
        const topline = element(
          'div',
          'f2w-event-card__topline'
        );

        topline.appendChild(
          element(
            'span',
            'f2w-event-card__period f2w-event-card__period--upcoming',
            typeLabel(venue.venueType)
          )
        );

        content.appendChild(topline);
      }

      content.appendChild(
        element(
          'h2',
          'f2w-event-card__title',
          venue.name
        )
      );

      if (venue.city) {
        content.appendChild(
          element(
            'p',
            'f2w-event-card__location',
            venue.city
          )
        );
      }

      if (venue.country) {
        const countryElement = element(
          'p',
          'f2w-event-card__location',
          venue.country
        );

        const flagElement =
          countryFlagElement(
            venue.countryCode,
            venue.country
          );

        if (flagElement) {
          countryElement.insertBefore(
            flagElement,
            countryElement.firstChild
          );
        }

        content.appendChild(
          countryElement
        );
      }

      if (
        venue.url &&
        labels.view
      ) {
        content.appendChild(
          element(
            'span',
            'f2w-event-card__view',
            labels.view
          )
        );
      }

      body.appendChild(content);
      card.appendChild(imageWrapper);
      card.appendChild(body);
      article.appendChild(card);

      return article;
    };

    const currentFilters = () => {
      return {
        search:
          searchInput?.value || '',

        country:
          countrySelect?.value || '',

        city: citySelect?.value || '',

        type:
          typeSelect?.value || ''
      };
    };

    const filteredVenues = (ignoreSearch = false) => {
      const filters = currentFilters();

      const search = ignoreSearch ? '' : normalize(filters.search);

      const country = normalize(
        filters.country
      );

      const type = normalize(
        filters.type
      );
      const city = normalize(filters.city);

      return venues.filter((venue) => {
        const searchText = normalize([
          venue.name,
          venue.city,
          venue.country,
          venue.venueType,
          typeLabel(venue.venueType)
        ].join(' '));

        const matchesSearch =
          !search ||
          searchText.includes(search);

        const matchesCountry =
          !country ||
          normalize(venue.country) ===
            country;

        const matchesType =
          !type ||
          normalize(venue.venueType) ===
            type;
        const matchesCity = !city || normalize(venue.city) === city;

        return (
          matchesSearch &&
          matchesCountry &&
          matchesCity &&
          matchesType
        );
      });
    };

    const updateUrl = () => {
      const url = new URL(
        window.location.href
      );

      const filters = currentFilters();

      const values = {
        q: filters.search.trim(),
        country: filters.country,
        city: filters.city,
        type: filters.type
      };

      Object.entries(values).forEach(
        ([key, value]) => {
          if (value) {
            url.searchParams.set(
              key,
              value
            );
          } else {
            url.searchParams.delete(key);
          }
        }
      );

      window.history.replaceState(
        window.history.state || {},
        '',
        url
      );
    };

    const renderArchive = () => {
      if (!resultsGrid) {
        return;
      }

      const filtered = filteredVenues();
      const searchWrap = root.querySelector('[data-f2w-venues-search-wrap]');
      const filtersWrap = root.querySelector('[data-f2w-directory-filters]');
      if (searchWrap) searchWrap.hidden = false;
      if (filtersWrap) filtersWrap.hidden = false;

      const visible = filtered.slice(
        0,
        visibleCount
      );

      resultsGrid.replaceChildren();

      visible.forEach((venue) => {
        resultsGrid.appendChild(
          createCard(venue)
        );
      });

      if (resultCount) {
        resultCount.textContent = [
          countFormatter.format(filtered.length),
          labels.results
        ]
          .filter(Boolean)
          .join(' ');
      }

      if (emptyElement) {
        setElementVisibility(
          emptyElement,
          exhausted &&
            !loadError &&
            filtered.length === 0
        );
      }

      if (loadMoreButton) {
        const hasMore =
          !loadError &&
          exhausted &&
          visible.length < filtered.length;

        setElementVisibility(
          loadMoreButton,
          hasMore
        );
        loadMoreButton.disabled = false;
        loadMoreButton.textContent =
          labels.loadMore;
      }

      updateUrl();
    };

    const rebuildSelect = (
      select,
      values,
      firstLabel,
      formatLabel
    ) => {
      if (!select) {
        return;
      }

      const current = select.value;

      select.replaceChildren();

      const firstOption =
        document.createElement('option');

      firstOption.value = '';
      firstOption.textContent =
        firstLabel;

      select.appendChild(firstOption);

      values.forEach((value) => {
        const option =
          document.createElement('option');

        option.value = normalize(value);
        option.textContent =
          typeof formatLabel === 'function'
            ? formatLabel(value)
            : value;

        select.appendChild(option);
      });

      const stillAvailable = [
        ...select.options
      ].some((option) => {
        return option.value === current;
      });

      select.value = stillAvailable
        ? current
        : '';
    };

    const buildFilters = () => {
      const countryPlaceholder =
        countrySelect?.options?.[0]
          ?.textContent || '';

      const typePlaceholder =
        typeSelect?.options?.[0]
          ?.textContent || '';

      rebuildSelect(
        countrySelect,
        uniqueSorted(
          [...venues.map((venue) => venue.country), ...pinnedNames.values()]
        ),
        countryPlaceholder
      );

      // Type options carry the raw machine value (arena, event_hall, ...)
      // and show the translated label from the section settings.
      rebuildSelect(
        typeSelect,
        uniqueSorted(
          venues.map((venue) => {
            return venue.venueType;
          })
        ),
        typePlaceholder,
        typeLabel
      );
    };

    const chip = (value, label, select, onSelect) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'f2w-event-card__sport';
      button.textContent = label;
      if (select === countrySelect && value) {
        const flag = countryFlagElement(countryCodes.get(value), label);
        if (flag) {
          flag.setAttribute('aria-hidden', 'true');
          const image = flag.querySelector('img');
          if (image) {
            image.alt = '';
            image.addEventListener('error', () => flag.remove(), { once: true });
          }
          button.prepend(flag);
        }
      }
      button.setAttribute('aria-pressed', String(select.value === value));
      button.addEventListener('click', () => {
        select.value = value;
        onSelect();
      });
      return button;
    };

    let featuredCountryValues = new Set();
    const renderCountryList = () => {
      const options = [...countrySelect.options].slice(1)
        .filter((option) => !featuredCountryValues.has(option.value));
      const buttons = options.map((option) => chip(option.value, option.textContent.trim(), countrySelect, selectCountry));
      if (!buttons.length) {
        const empty = document.createElement('p');
        empty.textContent = labels.countryEmpty;
        buttons.push(empty);
      }
      countryList.replaceChildren(...buttons);
    };

    const renderCountryPicker = () => {
      countryCodes.clear();
      for (const venue of venues) {
        if (venue.country && venue.countryCode) countryCodes.set(normalize(venue.country), venue.countryCode);
      }
      for (const [code, name] of pinnedNames) {
        if (name) countryCodes.set(normalize(name), code);
      }
      const options = [...countrySelect.options].slice(1);
      const counts = new Map();
      for (const venue of venues) {
        const key = normalize(venue.country);
        counts.set(key, (counts.get(key) || 0) + 1);
      }
      const pins = pinnedCodes.map((code) => options.find((option) => option.value === normalize(pinnedNames.get(code)))).filter(Boolean);
      const ranked = options.filter((option) => !pins.includes(option)).sort((a, b) =>
        (counts.get(b.value) || 0) - (counts.get(a.value) || 0)
          || a.textContent.localeCompare(b.textContent, language.toLowerCase()));
      const featured = [...pins, ...ranked.slice(0, 6 - pins.length)];
      const selected = countrySelect.value;
      if (selected && !featured.some((option) => option.value === selected)) {
        if (featured.length === 6) featured.pop();
        const active = options.find((option) => option.value === selected);
        if (active) featured.splice(pins.length, 0, active);
      }
      const fewCountries = options.length <= 6;
      const activeCountry = options.find((option) => option.value === selected);
      const visibleCountries = activeCountry ? [activeCountry] : featured;
      const expanded = !activeCountry && !countryPanel.hidden && !fewCountries;
      countryMore.hidden = Boolean(activeCountry) || fewCountries || expanded;
      featuredCountryValues = new Set(visibleCountries.map((option) => option.value));
      renderCountryList();
      const allChip = () => chip('', countrySelect.options[0].textContent.trim(), countrySelect, selectCountry);
      countryQuick.replaceChildren(
        allChip(),
        ...visibleCountries.map((option) => chip(option.value, option.textContent.trim(), countrySelect, selectCountry)),
        ...(expanded ? [countryPanel, countryMore] : [countryMore, countryPanel])
      );
      countryPanel.hidden = !expanded;
      countryMore.textContent = countryMoreLabel;
      countryPicker.hidden = false;
      root.dataset.f2wVenuesPickerReady = 'true';
    };

    let cityChoices = [];
    const renderCityList = () => {
      const query = normalize(citySearch.value);
      const options = [...citySelect.options];
      const matches = options.filter((option) => !query || normalize(option.textContent).includes(query));
      const buttons = matches.map((option) => chip(option.value, option.textContent.trim(), citySelect, selectCity));
      if (!buttons.length) {
        const empty = document.createElement('p');
        empty.textContent = labels.cityEmpty;
        buttons.push(empty);
      }
      cityList.replaceChildren(...buttons);
    };

    const renderCityPicker = () => {
      const ranked = [...cityChoices].sort((a, b) => b.count - a.count
        || a.name.localeCompare(b.name, language.toLowerCase()));
      const selected = ranked.find((entry) => entry.key === citySelect.value);
      cityQuick.replaceChildren(
        chip('', citySelect.options[0].textContent.trim(), citySelect, selectCity),
        ...(selected ? [chip(selected.key, selected.name, citySelect, selectCity)] :
          ranked.map((entry) => chip(entry.key, entry.name, citySelect, selectCity)))
      );
      cityPicker.hidden = !countrySelect.value || cityChoices.length < 2;
      cityMore.hidden = true;
      cityPanel.hidden = true;
      cityMore.setAttribute('aria-expanded', 'false');
    };

    const syncCity = () => {
      const previous = citySelect.value;
      const grouped = new Map();
      for (const venue of venues) {
        if (!countrySelect.value || normalize(venue.country) !== countrySelect.value) continue;
        const key = normalize(venue.city);
        if (!key) continue;
        if (!grouped.has(key)) grouped.set(key, { key, count: 0, variants: new Map() });
        const item = grouped.get(key);
        item.count++;
        item.variants.set(venue.city, (item.variants.get(venue.city) || 0) + 1);
      }
      cityChoices = [...grouped.values()].map((item) => ({
        key: item.key, count: item.count,
        name: [...item.variants].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], language.toLowerCase()))[0][0]
      }));
      const all = citySelect.options[0];
      citySelect.replaceChildren(all, ...cityChoices.sort((a, b) => a.name.localeCompare(b.name, language.toLowerCase()))
        .map((item) => new Option(item.name, item.key)));
      citySelect.value = cityChoices.some((item) => item.key === previous) ? previous : '';
      renderCityPicker();
    };

    const selectCountry = () => {
      citySelect.value = '';
      citySearch.value = '';
      cityPanel.hidden = true;
      cityMore.setAttribute('aria-expanded', 'false');
      countrySearch.value = '';
      countryPanel.hidden = Boolean(countrySelect.value);
      countryMore.setAttribute('aria-expanded', String(!countryPanel.hidden));
      syncCity();
      renderCountryPicker();
      changeFilter();
      (countryQuick.querySelector('[aria-pressed="true"]') || countryMore).focus({ preventScroll: true });
    };
    const selectCity = () => {
      citySearch.value = '';
      cityPanel.hidden = true;
      cityMore.setAttribute('aria-expanded', 'false');
      renderCityPicker();
      changeFilter();
      cityQuick.querySelector('[aria-pressed="true"]')?.focus({ preventScroll: true });
    };

    countryMore.addEventListener('click', () => {
      countryPanel.hidden = !countryPanel.hidden;
      countryMore.setAttribute('aria-expanded', String(!countryPanel.hidden));
      renderCountryPicker();
      if (!countryPanel.hidden) countryList.querySelector('button')?.focus({ preventScroll: true });
      else countryMore.focus({ preventScroll: true });
    });
    countryPanel.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      countryPanel.hidden = true;
      countryMore.setAttribute('aria-expanded', 'false');
      renderCountryPicker();
      countryMore.focus();
    });

    const applyUrlFilters = () => {
      const params =
        new URLSearchParams(
          window.location.search
        );

      if (searchInput) {
        searchInput.value =
          params.get('q') || '';
      }

      if (countrySelect) {
        countrySelect.value =
          params.get('country') || '';
      }

      syncCity();
      if (citySelect) citySelect.value = normalize(params.get('city') || '');
      renderCityPicker();
      if (typeSelect) {
        typeSelect.value =
          params.get('type') || '';
      }
    };

    const resetFilters = () => {
      if (searchInput) {
        searchInput.value = '';
      }

      if (countrySelect) {
        countrySelect.value = '';
      }

      if (typeSelect) {
        typeSelect.value = '';
      }
      citySelect.value = '';
      citySearch.value = '';
      countrySearch.value = '';
      cityPanel.hidden = true;
      countryPanel.hidden = true;
      cityMore.setAttribute('aria-expanded', 'false');
      countryMore.setAttribute('aria-expanded', 'false');
      syncCity();
      renderCountryPicker();
      visibleCount = perPage;
      renderArchive();
    };

    const changeFilter = () => {
      visibleCount = perPage;
      renderArchive();
    };

    searchInput?.addEventListener(
      'input',
      changeFilter
    );

    countrySelect?.addEventListener('change', selectCountry);
    citySelect?.addEventListener('change', selectCity);

    typeSelect?.addEventListener(
      'change',
      changeFilter
    );

    resetButton?.addEventListener(
      'click',
      resetFilters
    );

    loadMoreButton?.addEventListener(
      'click',
      () => {
        visibleCount += perPage;
        renderArchive();
      }
    );

    const start = async () => {
      try {
        if (loadingElement) {
          setElementVisibility(
            loadingElement,
            true
          );
          loadingElement.textContent =
            labels.loading;
        }

        if (errorElement) {
          setElementVisibility(
            errorElement,
            false
          );
        }

        let usedPublishedIndex = false;

        try {
          usedPublishedIndex =
            await loadPublishedIndex();
        } catch (indexError) {
          usedPublishedIndex = false;
          venues = [];
          cursor = null;
          exhausted = false;
        }

        if (!usedPublishedIndex) {
          await loadAllMatches();
        }

        for (const venue of venues) {
          const code = String(venue.countryCode || '').toUpperCase();
          if (pinnedCodes.includes(code) && venue.country) pinnedNames.set(code, venue.country);
        }
        try {
          const pinnedQuery = `query($ids:[ID!]!,$language:LanguageCode!) @inContext(language:$language) {
            nodes(ids:$ids) { ... on Metaobject { id name:field(key:"name") { value } } }
          }`;
          const countryData = await requestQuery(pinnedQuery, { ids: pinnedIds, language });
          (countryData.nodes || []).forEach((node, index) => {
            if (node?.name?.value && !pinnedNames.has(pinnedCodes[index])) pinnedNames.set(pinnedCodes[index], node.name.value);
          });
        } catch (error) {
          // The directory still works if an optional country-name lookup fails.
        }
        buildFilters();
        applyUrlFilters();
        renderCountryPicker();
        renderArchive();
      } catch (error) {
        loadError = true;

        if (resultsGrid) {
          resultsGrid.replaceChildren();
        }

        if (resultCount) {
          resultCount.textContent = '';
        }

        if (emptyElement) {
          setElementVisibility(
            emptyElement,
            false
          );
        }

        if (loadMoreButton) {
          setElementVisibility(
            loadMoreButton,
            false
          );
        }

        if (errorElement) {
          setElementVisibility(
            errorElement,
            true
          );
          errorElement.textContent =
            labels.error;
        }
      } finally {
        if (loadingElement) {
          setElementVisibility(
            loadingElement,
            false
          );
        }
      }
    };

    start();
  });
})();









